import { io } from "socket.io-client";
import API_CONFIG from "../config/api";
import authService from "./authService";

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.eventHandlers = new Map();
        this.connectionPromise = null;
    }

    // Initialize socket connection
    async connect() {
        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                const token = authService.isAuthenticated() ? localStorage.getItem("accessToken") : null;

                if (!token) {
                    reject(new Error("No authentication token available"));
                    return;
                }

                this.socket = io(API_CONFIG.SOCKET_URL, {
                    auth: { token },
                    // TEMPORARY: Use polling first until Nginx WebSocket support is configured
                    // Change back to ["websocket", "polling"] after Nginx is fixed
                    transports: ["polling", "websocket"],
                    timeout: 10000,
                    forceNew: true,
                    upgrade: true, // Allow upgrading to WebSocket after initial connection
                });

                this.setupEventHandlers();

                this.socket.on("connect", () => {
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    resolve(this.socket);
                });

                this.socket.on("connect_error", (error) => {
                    this.isConnected = false;

                    if (error.message === "Authentication failed") {
                        authService.clearUserData();
                        window.dispatchEvent(new CustomEvent("auth:logout"));
                    }

                    reject(error);
                });
            } catch (error) {
                reject(error);
            }
        });

        return this.connectionPromise;
    }

    // Disconnect socket
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            this.connectionPromise = null;
        }
    }

    // Setup default event handlers
    setupEventHandlers() {
        if (!this.socket) return;

        // Connection events
        this.socket.on("disconnect", (reason) => {
            this.isConnected = false;

            if (reason === "io server disconnect") {
                // Server initiated disconnect, don't reconnect
                this.disconnect();
            } else {
                // Client initiated disconnect, try to reconnect
                this.handleReconnect();
            }
        });

        // Authentication events
        this.socket.on("authenticated", () => {
        });

        this.socket.on("error", (error) => {
            this.handleError(error);
        });

        // Heartbeat
        this.socket.on("heartbeat", () => {
        });

        // Dashboard events
        this.socket.on("connected", () => {
        });
    }

    // Handle reconnection
    handleReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;

            setTimeout(() => {
                if (!this.isConnected && authService.isAuthenticated()) {
                    this.connect().catch(() => {});
                }
            }, 2000 * this.reconnectAttempts);
        }
    }

    // Handle errors
    handleError(error) {
        // Emit custom event for error handling in components
        window.dispatchEvent(new CustomEvent("socket:error", { detail: error }));
    }

    // Dashboard specific methods
    async joinDashboard() {
        if (!this.socket || !this.isConnected) {
            await this.connect();
        }

        this.socket.emit("join-dashboard");
    }

    leaveDashboard() {
        if (this.socket && this.isConnected) {
            this.socket.emit("leave-dashboard");
        }
    }

    // Widget specific methods
    joinWidget(widgetId) {
        if (this.socket && this.isConnected) {
            this.socket.emit("join-widget", widgetId);
        }
    }

    leaveWidget(widgetId) {
        if (this.socket && this.isConnected) {
            this.socket.emit("leave-widget", widgetId);
        }
    }

    // Request updates
    requestMetricsUpdate() {
        if (this.socket && this.isConnected) {
            this.socket.emit("request-metrics-update");
        }
    }

    requestActivitiesUpdate() {
        if (this.socket && this.isConnected) {
            this.socket.emit("request-activities-update");
        }
    }

    // Event subscription methods
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, new Set());
        }

        this.eventHandlers.get(event).add(handler);

        if (this.socket) {
            this.socket.on(event, handler);
        }

        // Return unsubscribe function
        return () => this.off(event, handler);
    }

    off(event, handler) {
        if (this.eventHandlers.has(event)) {
            this.eventHandlers.get(event).delete(handler);
        }

        if (this.socket) {
            this.socket.off(event, handler);
        }
    }

    // Remove all listeners for an event
    removeAllListeners(event) {
        if (this.eventHandlers.has(event)) {
            this.eventHandlers.delete(event);
        }

        if (this.socket) {
            this.socket.removeAllListeners(event);
        }
    }

    // Re-subscribe to events after reconnection
    resubscribeEvents() {
        if (!this.socket) return;

        for (const [event, handlers] of this.eventHandlers) {
            for (const handler of handlers) {
                this.socket.on(event, handler);
            }
        }
    }

    // Utility methods
    isSocketConnected() {
        return this.isConnected && this.socket && this.socket.connected;
    }

    getConnectionState() {
        return {
            connected: this.isConnected,
            socketId: this.socket?.id,
            reconnectAttempts: this.reconnectAttempts,
        };
    }

    // Cleanup method
    cleanup() {
        this.eventHandlers.clear();
        this.disconnect();
    }
}

// Create singleton instance
const socketService = new SocketService();

// Auto-cleanup on page unload
window.addEventListener("beforeunload", () => {
    socketService.cleanup();
});

// Auto-reconnect when authentication changes
window.addEventListener("auth:login", () => {
    if (!socketService.isSocketConnected()) {
        socketService.connect().catch(() => {});
    }
});

window.addEventListener("auth:logout", () => {
    socketService.cleanup();
});

export default socketService;